import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import EventImage from "@/components/EventImage";
import { Flame, ArrowRight, Headphones, ExternalLink } from "lucide-react";

const SECTION_LABELS = {
  echo: "Écho", podcast: "Podcast", article: "Article", rencontre: "Rencontre",
  reportage: "Reportage", culture: "Culture", media: "Média",
};

export const CampfireRelated = ({ type, slug }) => {
  const { data: items = [] } = useQuery({
    queryKey: ["campfire-related", type, slug],
    queryFn: async () => (await api.get("/campfire/related", { params: { type, slug } })).data,
    enabled: !!type && !!slug,
  });
  if (!items.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-16" data-testid="campfire-related">
      <h2 className="font-serif text-2xl sm:text-3xl text-amber-100 border-b border-amber-900/30 pb-4 mb-8 flex items-center gap-2">
        <Flame className="w-6 h-6 text-amber-500" /> Sur Campfire
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((a) => {
          const external = (a.section === "podcast" || a.section === "media") && a.external_url;
          const inner = (
            <div className="group bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full">
              <div className="relative h-40 overflow-hidden">
                <EventImage src={a.cover} alt={a.title} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase bg-amber-950/70 text-amber-300 border border-amber-800/40">{SECTION_LABELS[a.section] || "Campfire"}</span>
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg text-stone-100 leading-snug mb-2 line-clamp-2 group-hover:text-amber-100">{a.title}</h3>
                <p className="text-sm text-stone-400 line-clamp-2">{a.excerpt}</p>
                <div className="mt-4 flex items-center gap-1.5 text-amber-500 text-xs font-mono uppercase">
                  {external ? <><Headphones className="w-3.5 h-3.5" /> Écouter <ExternalLink className="w-3 h-3" /></> : <>Lire <ArrowRight className="w-3.5 h-3.5" /></>}
                </div>
              </div>
            </div>
          );
          return external
            ? <a key={a.slug} href={a.external_url} target="_blank" rel="noopener noreferrer" data-testid={`campfire-related-${a.slug}`} className="block h-full">{inner}</a>
            : <Link key={a.slug} to={`/campfire/${a.slug}`} data-testid={`campfire-related-${a.slug}`} className="block h-full">{inner}</Link>;
        })}
      </div>
    </section>
  );
};

export default CampfireRelated;

export const CampfireTopBanner = ({ type, slug }) => {
  const { data: items = [] } = useQuery({
    queryKey: ["campfire-related", type, slug],
    queryFn: async () => (await api.get("/campfire/related", { params: { type, slug } })).data,
    enabled: !!type && !!slug,
  });
  const a = items[0];
  if (!a) return null;
  const external = (a.section === "podcast" || a.section === "media") && a.external_url;
  const content = (
    <div className="group flex items-center gap-4 rounded-xl border border-amber-900/40 bg-gradient-to-r from-amber-950/40 to-[#121418] hover:border-amber-500/60 p-3 sm:p-4 transition-all">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden shrink-0"><EventImage src={a.cover} alt={a.title} imgClassName="w-full h-full object-cover" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500/90 mb-1 flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Sur Campfire · {SECTION_LABELS[a.section] || "Campfire"}</div>
        <div className="font-serif text-lg text-stone-100 leading-snug line-clamp-1 group-hover:text-amber-100">{a.title}</div>
        {a.excerpt && <p className="text-sm text-stone-400 line-clamp-1 mt-0.5">{a.excerpt}</p>}
      </div>
      <div className="shrink-0 text-amber-500 flex items-center gap-1 text-xs font-mono uppercase pr-1">
        {external ? <><Headphones className="w-4 h-4" /> <ExternalLink className="w-3 h-3" /></> : <ArrowRight className="w-4 h-4" />}
      </div>
    </div>
  );
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-6" data-testid="campfire-top-banner">
      {external
        ? <a href={a.external_url} target="_blank" rel="noopener noreferrer" data-testid={`campfire-top-${a.slug}`} className="block">{content}</a>
        : <Link to={`/campfire/${a.slug}`} data-testid={`campfire-top-${a.slug}`} className="block">{content}</Link>}
    </div>
  );
};

